import { IOperatorElearningTopicContentVm } from '../../../../../api/operator-elearning-topic-content-vm.model';
import { OperatorElearningTopicContentsService } from '../../../../../api/api-services/operator-elearning-topic-contents.service';
import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { MOBILE_LAYOUT_QUERY } from '../../../../shared/services/mobile-shell.service';
import { IFasTableDropdownFilter, IFasTableFilters } from 'fas-services';
import { Component, OnInit, inject } from '@angular/core';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-content-list',
  templateUrl: './content-list.component.html',
  styleUrl: './content-list.component.scss'
})
export class ContentListComponent implements OnInit {
  private _operatorElearningTopicContentsService = inject(OperatorElearningTopicContentsService);
  private _operatorElearningTopicsService = inject(OperatorElearningTopicsService);

  public originalTopicsContentList: IOperatorElearningTopicContentVm[] = [];
  public topicsContentList: IOperatorElearningTopicContentVm[] = [];
  public selectedTopicContent!: IOperatorElearningTopicContentVm;

  public isLoading = false;
  public currentRows = 10;
  public currentFirst = 0;
  public topicIndex = 0;
  public availableTopics: IOperatorElearningTopicVm[] = [];

  public dropdownFilters: IFasTableDropdownFilter[] = [];
  private _searchValue = '';
  private _selectedTopicIds: number[] = [];

  public detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  public topicColumns = [
    {
      field: 'slideOrder',
      header: 'Kolejność',
      contentClass: 'text-sm',
      sortable:true,
    },
    {
      field: 'imageLink',
      header: 'Slajd',
      contentClass: 'text-sm',
      customTemplate: 'slideImage',
    },
    {
      field: 'slideTitle',
      header: 'Tytuł slajdu',
      contentClass: 'text-sm',
      columnClass: 'name-col',
      sortable:true,
    },
    {
      field: 'elearningTopicName',
      header: 'Przypisany rozdział',
      contentClass: 'text-sm',
      sortable:true,
    },
    {
      field: 'translationsInfo',
      header: 'Wersje językowe',
      contentClass: 'text-sm',
      valueFunction: this.getTranslationsInfo.bind(this),
    },
    {
      field: 'created',
      header: 'Data utworzenia',
      contentClass: 'text-sm',
      dateField: true
    },
    {
      header: 'Akcje',
      contentClass: 'text-sm',
      actions: [
        {
          buttonClass: 'pi pi-eye cursor-pointer text-center appear transparent-button',
          callback: this.editTopic.bind(this),
          tooltip: 'Podgląd',
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.loadPaginationState();
    this.loadAvailableTopics();
    this.loadTopics();
  }

  private loadAvailableTopics(): void {
    this._operatorElearningTopicsService.get().subscribe({
      next: (topics) => {
        this.availableTopics = topics;
        this.dropdownFilters = [
          {
            id: 'topics',
            placeholder: 'Filtruj po rozdziale',
            options: topics.map((t) => ({ label: t.name, value: t.id })),
          },
        ];
      },
      error: (error) => {
        this.availableTopics = [];
      },
    });
  }

  public loadTopics(): void {
    this.isLoading = true;
    this._operatorElearningTopicContentsService
      .get(undefined, this._searchValue || undefined, this._selectedTopicIds.length ? this._selectedTopicIds : undefined)
      .subscribe({
        next: (topics) => {
          this.originalTopicsContentList = topics;
          this.topicsContentList = topics;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        }
      });
  }

  public onFiltersChange(filters: IFasTableFilters): void {
    this._searchValue = filters.searchString ?? '';
    this._selectedTopicIds = filters.dropdownValues?.['topics'] ?? [];
    this.loadTopics();
  }

  public getSlideImageUrl(imageLink: string): string {
    return environment.webApiUrl + imageLink.replace('/media', '/uploadMedia');
  }

  public getTranslationsInfo(topic: IOperatorElearningTopicContentVm): string {
    if (topic.elearningTopicContentTranslation && topic.elearningTopicContentTranslation.length > 0) {
      const nonPolishTranslations = topic.elearningTopicContentTranslation.filter((t: any) => t.languageCode !== 'pl');

      if (nonPolishTranslations.length > 0) {
        const languages = nonPolishTranslations.map((t: any) => t.languageCode?.toUpperCase()).join(', ');
        return `${languages}`;
      } else {
        return 'Brak tłumaczeń';
      }
    }
    return 'Brak tłumaczeń';
  }

  /** On a phone the preview button is in the last column, off the screen: a tap on the row opens the slide. */
  public onRowTap = (topic: any, index: number): void => {
    if (window.matchMedia(MOBILE_LAYOUT_QUERY).matches) {
      this.editTopic(topic, index);
    }
  };

  public editTopic(topic: any, index: number): void {
    const originalTopicContent = this.originalTopicsContentList.find(t => t.id === topic.id);
    if (originalTopicContent) {
      this.selectedTopicContent = originalTopicContent;
      this.topicIndex = this.originalTopicsContentList.findIndex(t => t.id === topic.id);
      this.detailsControl.isOpen = true;
      this.detailsControl.isEditing = false;
    }
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
  }

  public refreshTopics(): void {
    this.loadAvailableTopics();
    this.loadTopics();
  }

  public setCurrentPosition(event: any): void {
    this.currentFirst = event.first;
    this.currentRows = event.rows;
    this.savePaginationState();
  }

  private loadPaginationState(): void {
    const savedState = localStorage.getItem('elearning-content-pagination');
    if (savedState) {
      const state = JSON.parse(savedState);
      this.currentFirst = state.currentFirst || 0;
      this.currentRows = state.currentRows || 10;
    }
  }

  private savePaginationState(): void {
    const state = {
      currentFirst: this.currentFirst,
      currentRows: this.currentRows
    };
    localStorage.setItem('elearning-content-pagination', JSON.stringify(state));
  }
}
